/**
 *  Navigation
 *    Scripts to control navigation's functionality on the site for both Desktop
 *    and Mobile.
 *
 *  Version:
 *    1.0.0 - 2024/04/07
 */

import { CLASS_ACTIVE, CLASS_SCROLL_DISABLED, CLASS_VISIBLE, CLASS_SELECTED,
  CLASS_HAS_SELECTION } from "../../tools/constants/classes";

export const SELECTOR_NAVIGATION = '[data-navigation]';
export const SELECTOR_NAVIGATION_TOGGLE = '[data-navigation-toggle]';
const SELECTOR_NAVIGATION_CONTENT = '[data-navigation-content]';
const SELECTOR_MENU_TOGGLE = '[data-menu-toggle]';
const SELECTOR_MENU_ITEM = '[data-menu-item]';
const SELECTOR_SUBMENU = '[data-submenu]';
const SELECTOR_SUBMENU_BACK = '[data-submenu-back]';
const SELECTOR_SUBMENU_LIST = '[data-submenu-list]';

// ATTRIBUTES
const ATTR_MENU_HANDLE = 'data-menu-handle';
const ATTR_MOBILE_TRIGGER = 'data-mobile-trigger';
const ATTR_TIER_COUNT = 'data-tier-count';
const ATTR_MENU_ITEM_HOVER = 'data-menu-item-hover';

export class Navigation {
  container: HTMLElement;
  navigationContent: HTMLElement | null = null;
  navigationToggles: NodeListOf<HTMLElement>;
  menuToggles: HTMLElement[] = [];
  menuItems: HTMLElement[] = [];
  submenus: NodeListOf<HTMLElement>;
  submenuBackButtons: NodeListOf<HTMLElement>;
  submenuLists: NodeListOf<HTMLElement>;
  
  constructor(container:HTMLElement) {
    this.container = container;
    this.navigationContent = this.container.querySelector(SELECTOR_NAVIGATION_CONTENT);
    this.navigationToggles = this.container.querySelectorAll<HTMLElement>(SELECTOR_NAVIGATION_TOGGLE);
    this.submenus = this.container.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU);
    this.submenuBackButtons = this.container.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_BACK);
    this.submenuLists = this.container.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU_LIST);

    this.menuToggles = Array.from(
      this.container.querySelectorAll<HTMLElement>(SELECTOR_MENU_TOGGLE)
    );

    this.menuItems = Array.from(
      this.container.querySelectorAll<HTMLElement>(SELECTOR_MENU_ITEM)
    );

    this.attachEvents();
  }

  attachEvents() {
    this.navigationToggles.forEach(element => {
      element.addEventListener("click", this.onToggleClicked.bind(this));

      if (element.classList.contains('is-inner')) {
        element.addEventListener('mouseover', () => {
          this.navigationToggles.forEach(btn => {
            btn.classList.add('is-hover');
          });
        });
        element.addEventListener('mouseleave', () => {
          this.navigationToggles.forEach(btn => {
            btn.classList.remove('is-hover');
          });
        });
      }
    });

    // Click
    this.menuToggles.forEach(element => {
      const parentMenuItem = element.closest(SELECTOR_MENU_ITEM);

      if (parentMenuItem && parentMenuItem.getAttribute(ATTR_MENU_ITEM_HOVER) != "true") {
        element.addEventListener("click", this.onMenuToggleClicked.bind(this));
      }
    })

    // Hover
    this.menuItems.forEach(element => {
      if (element.getAttribute(ATTR_MENU_ITEM_HOVER) == "true") {
        if(window.innerWidth >= 1100) {
          element.addEventListener("mouseover", this.onMouseOver.bind(this));
          element.addEventListener("mouseleave", this.onMouseLeave.bind(this));
        }
        else {
          element.addEventListener("click", this.onMouseOver.bind(this));
        }
      }
    })

    this.submenuBackButtons.forEach(element => {
      element.addEventListener("click", this.onBackClicked.bind(this));
    })
  }

  detachEvents() {
    this.navigationToggles.forEach(element => {
      element.removeEventListener("click", this.onToggleClicked.bind(this));
    });

    this.menuToggles.forEach(element => {
      element.removeEventListener("click", this.onMenuToggleClicked.bind(this));
    })

    this.submenuBackButtons.forEach(element => {
      element.removeEventListener("click", this.onBackClicked.bind(this));
    })
  }

  // Event Handlers

  onToggleClicked(e: Event) {
    e.preventDefault();
    const targetElement = e.currentTarget as HTMLElement;
    
    this.clearActiveMenu();
    this.clearVisibleSubmenu();
    this.clearListSelection();
    this.setActiveMenu(e, true);
    this.toggle();

    // Initialize active menu when the trigger is the mobile nav toggle
    if (targetElement.getAttribute(ATTR_MOBILE_TRIGGER) == "true") {
      this.resetVisibleSubMenu();
    }

    this.setTierCount();

    this.navigationToggles.forEach(btn => {
      btn.classList.remove('is-hover');
    });
  }

  onMenuToggleClicked(e: Event) {
    e.preventDefault();

    // Query item's submenu
    const targetElement = e.target as HTMLElement;
    const menuItem = targetElement.closest(SELECTOR_MENU_ITEM);
    const submenu = menuItem?.querySelector(SELECTOR_SUBMENU);

    this.clearActiveMenu(submenu as HTMLElement);
    this.setActiveMenu(e);

    this.setListSelection(menuItem as HTMLElement);
    menuItem?.classList.add(CLASS_SELECTED);
    submenu?.classList.add(CLASS_VISIBLE);

    this.setTierCount();
  }

  onBackClicked(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    const targetElement = e.target as HTMLElement;
    const submenu = targetElement.closest(SELECTOR_SUBMENU);

    // Clear selection of parent menu
    const parentMenuItem = submenu?.closest<HTMLElement>(SELECTOR_MENU_ITEM);
    if (parentMenuItem) this.clearListSelection(parentMenuItem);

    // Hide current submenu
    submenu?.classList.remove(CLASS_VISIBLE);

    this.resetVisibleSubMenu();
    this.setTierCount();
  }

  onMouseOver(e: Event) {
    // Query item's submenu
    e.stopImmediatePropagation();
    const menuItem = e.currentTarget as HTMLElement;
    if(window.innerWidth < 1100 && menuItem.getAttribute('data-collection-item')) return;
    const submenu = menuItem?.querySelector(SELECTOR_SUBMENU);

    this.clearActiveMenu(submenu as HTMLElement);
    this.setActiveMenu(e);

    this.setListSelection(menuItem as HTMLElement);
    menuItem?.classList.add(CLASS_SELECTED);
    submenu?.classList.add(CLASS_VISIBLE);

    this.setTierCount();
  }

  onMouseLeave(e: Event) {
    const targetElement = e.currentTarget as HTMLElement;

    // Clear selection
    this.clearListSelection(targetElement);
    
    const submenu = targetElement?.querySelector<HTMLElement>(SELECTOR_SUBMENU);
    submenu?.classList.remove(CLASS_VISIBLE);

    this.resetVisibleSubMenu();
    this.setTierCount();
  }

  // Private

  private clearActiveMenu(exceptionElement: HTMLElement | null = null) {
    this.menuItems
    .filter(element => element.classList.contains(CLASS_ACTIVE))
    .forEach(element => {
      if (element.contains(exceptionElement)) return;
      element.classList.remove(CLASS_ACTIVE);

      // Clear visible menu of current active element
      this.clearVisibleSubmenu(element);
    })
  }

  private clearVisibleSubmenu(referenceElement: HTMLElement | null = null) {
    if (referenceElement) {
      referenceElement.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU).forEach(element => {
        element.classList.remove(CLASS_VISIBLE);
      })
    } else {
      // Clear all
      this.submenus.forEach(element => {
        element.classList.remove(CLASS_VISIBLE);
      })
    }
    
  }

  private setActiveMenu(e: Event, initializeSubmenu = false) {
    const targetElement = e.currentTarget as HTMLElement;
    
    // Get active handle
    const handle = targetElement.getAttribute(ATTR_MENU_HANDLE);
    if (!handle) return; // Proceed only when handle is present

    // Search for matching handle
    const activeMenuToggle = this.menuToggles.find(element => {
      const elementHandle = element.getAttribute(ATTR_MENU_HANDLE);
      if (elementHandle == handle) return element;
    })
    
    if (!activeMenuToggle) return;

    // Clear list selection
    this.clearListSelection();
    this.setActiveMenuClass(activeMenuToggle);

    if (initializeSubmenu) {
      const activeMenuItem = activeMenuToggle.closest(SELECTOR_MENU_ITEM);
      activeMenuItem?.querySelector(SELECTOR_SUBMENU)?.classList.add(CLASS_VISIBLE);
    }
  }

  private setActiveMenuClass(reference: HTMLElement) {
    // Search for toggle's menu item and set to active
    const activeMenuItem = reference.closest(SELECTOR_MENU_ITEM);
    activeMenuItem?.classList.add(CLASS_ACTIVE);
  }

  private toggle() {
    if(this.container.classList.contains(CLASS_VISIBLE)) {
      this.container.classList.remove(CLASS_VISIBLE);
      document.body.classList.remove(CLASS_SCROLL_DISABLED);
      setTimeout(() => {
        this.container.classList.remove(CLASS_ACTIVE);
      }, 100);
    } else if(!this.container.classList.contains(CLASS_ACTIVE)) {
      this.container.classList.add(CLASS_ACTIVE);
      // allow rendering of animation from display: none;
      setTimeout(() => {
        this.container.classList.add(CLASS_VISIBLE);
      }, 1);
      document.body.classList.add(CLASS_SCROLL_DISABLED);
    }
  }

  private resetVisibleSubMenu() {
    const hasVisibleSubmenu = Array.from(this.submenus).some(element => {
      return element.classList.contains(CLASS_VISIBLE);
    });

    if (!hasVisibleSubmenu) {
      const submenu = this.submenus[0];
      if (!submenu) return;

      this.clearActiveMenu();
      this.clearVisibleSubmenu();
      submenu.classList.add(CLASS_VISIBLE);
      this.setActiveMenuClass(submenu);
    }
  }

  private setListSelection(menuItem: HTMLElement) {
    const listElement = menuItem.closest(SELECTOR_SUBMENU_LIST);
    listElement?.classList.add(CLASS_HAS_SELECTION);
  }

  private clearListSelection(referenceElement: HTMLElement | null = null) {
    if (referenceElement) {
      referenceElement.classList.remove(CLASS_SELECTED);

      const parentList = referenceElement.closest(SELECTOR_SUBMENU_LIST);
      parentList?.classList.remove(CLASS_HAS_SELECTION);
    } else {
      this.submenuLists.forEach(element => {
        element.classList.remove(CLASS_HAS_SELECTION);
      })

      this.menuItems.forEach(element => {
        element.classList.remove(CLASS_SELECTED);
      })
    }
  }

  private setTierCount() {
    const visibleSubmenus = Array.from(this.submenus).filter(element => {
      return element.classList.contains(CLASS_VISIBLE);
    });

    if (this.navigationContent) {
      this.navigationContent.setAttribute(ATTR_TIER_COUNT, `${visibleSubmenus.length}`);
    }
  }
}